/**
 * getChatPreviewText — pure `ChatMessageType × sender → preview string` mapping
 * for the Inbox direct-message row.
 *
 * Flutter source of truth: `MessagesChatItem`
 * (`lib/presentation/screens/messages/widgets/messages_chat_item.dart:34-82`).
 *
 * Extracted from `InboxChatItem.tsx` so the Flutter-parity preview mapping is
 * unit-testable on its own and the row stays under the size threshold.
 *
 * own → "You: …"/"You sent …", peer → "<peerFirstName> sent …" where
 * `peerFirstName = nameUser1.split(' ')[0]` (`messages_chat_item.dart:32`).
 */
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { l10n } from '@/presentation/locale/l10n';

export function getChatPreviewText(
  node: ChatMessageNodeEntity,
  isCurrentUser: boolean,
  c: l10n['inbox']['chat'],
): string {
  // Always the sender's first name (`messages_chat_item.dart:32`) — only used
  // in the peer branch, where the sender is `idFrom` (`nameUser1`).
  const peerFirstName = node.nameUser1.split(' ')[0];

  switch (node.type) {
    case ChatMessageType.text:
      return isCurrentUser
        ? c.youColon.replace('{content}', node.lastContent)
        : node.lastContent;
    case ChatMessageType.image:
      return isCurrentUser
        ? c.youSentImage
        : c.sentImage.replace('{name}', peerFirstName);
    case ChatMessageType.video:
      return isCurrentUser
        ? c.youSentVideo
        : c.sentVideo.replace('{name}', peerFirstName);
    case ChatMessageType.gift:
      return isCurrentUser
        ? c.youSentGift
        : c.sentGift.replace('{name}', peerFirstName);
    case ChatMessageType.videoCall:
      return isCurrentUser
        ? c.youStartedVideoCall
        : c.startedVideoCall.replace('{name}', peerFirstName);
    case ChatMessageType.shareUser:
      return isCurrentUser
        ? c.youSharedUserProfile
        : c.sharedUserProfile.replace('{name}', peerFirstName);
    case ChatMessageType.sharePhoto:
      return isCurrentUser
        ? c.youSharedPhoto
        : c.sharedPhoto.replace('{name}', peerFirstName);
    case ChatMessageType.shareVideo:
      return isCurrentUser
        ? c.youSharedVideo
        : c.sharedVideo.replace('{name}', peerFirstName);
    default:
      return '';
  }
}
